import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { fmtEUR, fmtDate } from "../../lib/format";

const STATUSES = ["", "pending_payment", "paid", "provisioning", "fulfilled", "provisioning_failed", "cancelled", "refunded"];

export default function AdminOrders() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [refunding, setRefunding] = useState(null);

  const load = () => {
    setLoading(true);
    const q = status ? `?status=${status}` : "";
    api.get(`/admin/orders${q}`).then(({ data }) => setOrders(data)).finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const retry = async (id) => {
    await api.post(`/admin/orders/${id}/retry`);
    setMsg(t("admin.retrySent"));
    setTimeout(() => { setMsg(""); load(); }, 1500);
  };

  const refund = async (id) => {
    if (!window.confirm(t("admin.refundConfirm", "Refund this order in full? This cannot be undone."))) return;
    setRefunding(id);
    try {
      const { data } = await api.post(`/admin/orders/${id}/refund`);
      setMsg(t("admin.refundOk", "Refunded €") + (data.amount_eur ?? "").toFixed?.(2) ?? data.amount_eur);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Refund failed");
    } finally {
      setRefunding(null);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="overline mb-2">{t("admin.orders")}</div>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tightest mb-8">{t("admin.orders")}</h1>
      <div className="mb-6 flex items-center gap-3">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-black/20 px-3 py-2 text-sm" data-testid="admin-orders-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        {msg && <span className="text-sm text-success">{msg}</span>}
      </div>
      <div className="card-soft overflow-auto !rounded-3xl">
        <table className="w-full text-sm" data-testid="admin-orders-table">
          <thead className="bg-cobalt-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">ID</th>
              <th className="text-left px-4 py-3">{t("common.email")}</th>
              <th className="text-left px-4 py-3">{t("common.plans")}</th>
              <th className="text-left px-4 py-3">{t("dash.amount")}</th>
              <th className="text-left px-4 py-3">{t("common.status")}</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-ink-500">{t("common.loading")}</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-ink-100" data-testid={`admin-order-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.id.slice(0, 8)}…</td>
                <td className="px-4 py-3">{o.email}</td>
                <td className="px-4 py-3">{o.plan_snapshot?.name}</td>
                <td className="px-4 py-3 font-semibold">{fmtEUR(o.amount)}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-ink-200 bg-ink-100">{o.status.replace(/_/g, " ")}</span>
                </td>
                <td className="px-4 py-3 text-ink-500">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-3 space-x-3">
                  {o.status === "provisioning_failed" && (
                    <button onClick={() => retry(o.id)} className="text-xs font-semibold text-klein hover:underline" data-testid={`admin-retry-${o.id}`}>
                      {t("admin.retryProv")}
                    </button>
                  )}
                  {o.payment_status === "paid" && o.status !== "refunded" && !o.refund_id && (
                    <button
                      onClick={() => refund(o.id)}
                      disabled={refunding === o.id}
                      className="text-xs font-semibold text-destructive hover:underline disabled:opacity-50"
                      data-testid={`admin-refund-${o.id}`}
                    >
                      {refunding === o.id ? "…" : t("admin.refund", "Refund")}
                    </button>
                  )}
                  {o.refund_id && (
                    <span className="text-xs text-ink-500" title={o.refund_id}>↩ {t("admin.refunded", "Refunded")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
